import React, { useState, useEffect, useContext, useCallback } from 'react';
import { t } from 'i18next';
import {
  SelectDropdown,
  Collapsible,
  Button,
  Paragraph,
  ErrorMessage,
  GlobalSuccess,
} from '@sentara-health/optimahealth_web';

import {
  preferencesDetails,
  useDeviceType,
  DeviceTypes,
  ApiWrapper,
  errorhandler,
  domainForSegment,
  ANALYTICS_MESSAGES,
} from 'common';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { ProfileHeaderContent, ProfileInformations } from 'templates';
import {
  LanguagesList,
  Languages,
} from '@sentara-health/sentara-api-hooks-core/dist/typings/models/ciamPreferences';
import { useForm } from 'react-hook-form';
import { GlobalContext } from 'context';
import {
  usePreferenceUpdate,
  usePreferenceLanguage,
} from '@sentara-health/sentara-api-hooks-core';

const LanguagePreference = ({
  state,
  setState,
  languagePreference,
  expand = true,
  onExpand,
  onCollapse,
}: {
  languagePreference: string;
  expand?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  state: ProfileInformations;
  setState: React.Dispatch<React.SetStateAction<ProfileInformations>>;
}) => {
  const [loader, setLoader] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [languageData, setLanguageData] = useState<any[]>([]);
  const { fetchPreferenceLanguage } = usePreferenceLanguage();
  const { data: languageList, callApi: prefCallApi } = ApiWrapper(
    fetchPreferenceLanguage
  );
  const { contentListingItemClicked } = useAdobeAnalytics();

  const trackContentListing = (
    contentId: string,
    contentTitle: string,
    itemPosition: number
  ) => {
    contentListingItemClicked({
      segment: `${domainForSegment}/profile`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [{ contentId, contentTitle, itemPosition }],
        },
      },
    });
  };

  const { updatePreference } = usePreferenceUpdate();

  const defaultLanguageList = [
    {
      displayText: 'English',
      value: 'en_US',
      displayOrder: 1,
      isDefault: true,
    },
    {
      displayText: 'Spanish',
      value: 'es_ES',
      displayOrder: 2,
      isDefault: false,
    },
  ];

  useEffect(() => {
    if (languageList) {
      setLanguageData((languageList as LanguagesList)?.languages ?? []);
      const currentLanguage = (
        languageList as LanguagesList
      )?.languages?.filter(
        (lang: { isDefault: string }) => lang.isDefault
      ) as Languages[];
      setDefaultLanguage(
        currentLanguage[0]?.displayText ?? preferencesDetails.English
      );
      setTimeout(() => {
        setValue('language', languagePreference);
      }, 0);
    }
  }, [languageList, languagePreference]);

  const fetchPreferenceLanguageData = async () => {
    setLoader(true);
    try {
      await prefCallApi();
    } catch (error) {
      console.error('Error fetching language preference:', error);
    } finally {
      setLoader(false);
    }
  };

  const deviceType = useDeviceType();
  const { loginMemberId, getFeature } = useContext(GlobalContext);
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const {
    handleSubmit,
    register,
    formState: { isDirty },
    reset,
    setValue,
    getValues,
    watch
  } = useForm<{ language: string }>({
    defaultValues: {
      language: languagePreference,
    },
  });

  useEffect(() => {
    if (languagePreference) {
      setValue('language', languagePreference);
    }
  }, [languagePreference, languageList]);

  const childrenData = (
    <>
      {languageData?.map(
        (data: { value: string; displayText: string; isDefault: boolean }) => (
          <option
            key={data.value}
            data-testid="select-option"
            value={data.displayText}
          >
            {data.displayText}
          </option>
        )
      )}
    </>
  );

  const onClickEdit = useCallback(() => {
    trackContentListing('619', ANALYTICS_MESSAGES.LANGUAGE_PREF_EDIT, 22);
    if (getFeature?.LanguagePreferenceList && !languageList) {
      fetchPreferenceLanguageData();
    } else if (getFeature?.LanguagePreferenceList === false) {
      setLanguageData(defaultLanguageList);
      const currentLanguage = defaultLanguageList.filter(
        (lang: { isDefault: boolean }) => lang.isDefault
      );
      setDefaultLanguage(
        currentLanguage[0]?.displayText || preferencesDetails.English
      );
      setTimeout(() => {
        setValue('language', languagePreference);
      }, 0);
    }
    setIsEdit(true);
  }, [languageList]);

  const onClickCancel = () => {
    trackContentListing('621', ANALYTICS_MESSAGES.LANGUAGE_PREF_CANCEL, 24);
    setIsEdit(false);
    setValue(
      'language',
      state.languagePreference ?? preferencesDetails.English
    );
  };
  const onChangeLanguage = async () => {
    setIsEdit(false);
    setLoader(true);
    const apiPayload = {
      resourceType: 'profile',
      active: true,
      identifier: [
        {
          value: loginMemberId,
          system: 'memberId',
        },
      ],
      preferences: [
        {
          identifier: [
            {
              value: 'Language preference',
              system: 'https://sentara/possibleNow/identifier/preferencetype',
            },
          ],
          option: getValues('language'),
        },
      ],
    };

    const response = await updatePreference(apiPayload);
    if (response?.errorCode) {
      errorhandler(response?.errorCode);
      setLoader(false);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 6000);
    } else {
      setState({
        ...state,
        languagePreference: getValues('language'),
      });
      reset({
        language: getValues("language"),
      });
      trackContentListing('620', ANALYTICS_MESSAGES.LANGUAGE_PREF_SAVE, 23);
      setLoader(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 6000);
    }
  };
  return (
    <Collapsible
      title={t('profile.languagePreference')}
      id={'emailPaperless'}
      isMobile={isMobile}
      key={`${'emailPaperless'}-${isMobile ? 'mobile' : 'desktop'}`}
      expand={expand}
      isLoading={loader}
      onExpand={onExpand}
      onCollapse={onCollapse}
      className="mb-3 pb-2"
    >
      <div className="set_my_preferences">
        <div className="user_address_wrapper py-4 border_wrapper">
          <ProfileHeaderContent
            header={''}
            contentOne={t('profile.langPrefDesc')}
            dataTestIdOne="languagePreferenceDescription"
            contentClassNameOne={'mb-sm-0'}
            showButton={!isEdit}
            onClick={() => onClickEdit()}
            disableButton={!state?.isMemberEnrolled}
          />
          {isEdit && (
            <>
              <Paragraph
                className="form-check-label lineBrake"
                text={t('profile.langPrefDesc2')}
              />
              <Paragraph
                text={t('profile.preferredLanguage')}
                variant="bold"
                className="mb-2 mt-2"
              />
              <Paragraph
                text={
                  languagePreference !== defaultLanguage
                    ? languagePreference
                    : defaultLanguage + ' (Default)'
                }
                variant="regular"
              />
              {languageData?.length > 0 && (
                <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                  <SelectDropdown
                    dataTestId="languageSelect"
                    className={`form-select`}
                    id="languageSelect"
                    value={watch('language') ?? ''} // fix for co-browse issue where value shows the last selected value instead of the current value
                    {...register('language', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        e.preventDefault();
                        setValue('language', e.target.value);
                      },
                    })}
                  >
                    {childrenData}
                  </SelectDropdown>
                </div>
              )}
              <Paragraph
                variant="medium"
                className="pt-4"
                level={3}
                data-testid="preferredLanguageDesc"
                text={
                  <>
                    {t('profile.preferredLanguageDesc')}

                    {t('profile.langPrefTelephone')}
                  </>
                }
              />
              <div className="row d-flex justify-content-start align-items-center py-2">
                <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                  <Button
                    dataTestId={'languageSaveButton'}
                    variant="primary_btn"
                    disabled={!isDirty}
                    type={'submit'}
                    onClick={handleSubmit(onChangeLanguage)}
                  >
                    {t('common.save')}
                  </Button>
                </div>
                <div className="col-sm-auto col-12 mb-sm-0">
                  <Button
                    dataTestId={'languageCancelButton'}
                    variant="secondary_btn"
                    onClick={onClickCancel}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </>
          )}
          {showError && (
            <div className="mt-1" data-testid="language_preference_error">
              <ErrorMessage>{t('profile.languageSaveError')}</ErrorMessage>
            </div>
          )}
          {showSuccess && (
            <div className="fade-toast">
              <GlobalSuccess
                variant="mb-3"
                dataTestId="language_preference_success"
              >
                {t('profile.languageSaveSuccess', {
                  language: getValues('language'),
                })}
              </GlobalSuccess>
            </div>
          )}
        </div>
      </div>
    </Collapsible>
  );
};

export default LanguagePreference;
